import { Box, Button, Grid, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { PrivateLoader } from "src/components/templates";
import { SelectInput, TextFieldLabel } from "src/components/UI";
import { SelectInputOption } from "src/components/UI/CustomUI/atoms/SelectInput/types";
import Modal from "src/components/UI/CustomUI/molecules/Modal";
import ProfilePicture from "src/components/UI/CustomUI/molecules/ProfilePicture";
import { employeesActions, filesActions } from "src/ducks/actions";
import { EmployeeRole } from "src/ducks/employee/types";
import { EMAIL_TYPE, isEmpty, PHONE_MASK_INPUT } from "src/helpers";

interface CreateEmployeeModalProps {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    rolesOptions: SelectInputOption[];
}

export default function CreateEmployeeModal({ open, setOpen, rolesOptions }: CreateEmployeeModalProps) {
    const dispatch = useDispatch();

    // Required fields
    const [email, setEmail] = useState<string>('');
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [selectedRoles, setSelectedRoles] = useState<SelectInputOption[]>([]);

    // Optional fields
    const [phone, setPhone] = useState<string>('');
    const [line1, setLine1] = useState<string>('');
    const [line2, setLine2] = useState<string>('');
    const [city, setCity] = useState<string>('');
    const [state, setState] = useState<string>('');
    const [zipCode, setZipCode] = useState<string>('');
    const [picture, setPicture] = useState<string>('');

    const [isLoading, setIsLoading] = useState(false);
    const [upLoading, setUploading] = useState(false);
    const [showWarnings, setShowWarnings] = useState(false);

    const handleUpload = (image: File | null): void => {
        if (image) {
            setUploading(true);
            dispatch(
                filesActions.uploadFile(image, (uploadSucc, fileName) => {
                    if (uploadSucc && !isEmpty(fileName)) {
                        setPicture(fileName as string);
                        setTimeout(() => {
                            setUploading(false);
                        }, 2000);
                    } else {
                        setPicture('');
                        setUploading(false);
                    }
                })
            );
        }
    };

    const validateForm = (): boolean => {
        return !isEmpty(email) && !isEmpty(firstName) && !isEmpty(lastName) && selectedRoles.length > 0;
    };

    const resetForm = () => {
        setEmail('');
        setFirstName('');
        setLastName('');
        setSelectedRoles([]);
        setPhone('');
        setLine1('');
        setLine2('');
        setCity('');
        setState('');
        setZipCode('');
        setPicture('');
        setShowWarnings(false);
    };

    const handleCreate = () => {
        if (!validateForm()) {
            setShowWarnings(true);
            return;
        }
        setIsLoading(true);

        const employeeData: any = {
            email: [{ email, emailType: EMAIL_TYPE.PRIMARY }],
            firstName,
            lastName,
            roles: selectedRoles.map((role, index) => ({
                roleName: role.label,
                roleType: role.key,
                primary: index === 0
            } as EmployeeRole)),
            ...(phone && { phone: Number(phone) }),
            ...(picture && { picture }),
            ...((line1 || line2 || city || state || zipCode) && {
                address: {
                    line1: line1 || '',
                    line2: line2 || '',
                    city: city || '',
                    state: state || '',
                    zipCode: zipCode || '',
                }
            })
        };

        dispatch(employeesActions.createEmployeeNew({ employee: employeeData }, (success: boolean) => {
            if (success) {
                resetForm();
                toast.success('Employee created successfully');
                dispatch(employeesActions.fetchEmployees({ searchParams: { search: '' } }, (success: boolean) => {
                }));
                setOpen(false);
            } else {
                setIsLoading(false);
            }
        }));
    };

    const handleCancel = () => {
        resetForm();
        setOpen(false);
    };

    const isFormValid = validateForm();

    return (
        <Modal
            open={open}
            setOpen={setOpen}
            title={
                <Grid container justifyContent='left' padding='1rem'>
                    <Typography variant='h5Bold'>Add Employee</Typography>
                </Grid>
            }
        >
            <PrivateLoader loading={isLoading}>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <PrivateLoader loading={upLoading}>
                            <ProfilePicture avatar={picture} onUpload={handleUpload} uploading={upLoading} />
                        </PrivateLoader>

                        {upLoading && (
                            <Box
                                sx={{
                                    zIndex: 10,
                                    position: 'absolute',
                                    borderRadius: '32px',
                                    top: '60px',
                                    left: '25px',
                                    width: '180px',
                                    height: '180px',
                                    backgroundColor: 'rgba(192, 192, 192, 0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            ></Box>
                        )}
                    </Grid>
                    <Grid item xs={6} container spacing={2}>
                        <Grid item xs={12}>
                            <TextFieldLabel
                                label="Email *"
                                type="email"
                                value={email}
                                error={showWarnings && isEmpty(email)}
                                onChange={(event) => setEmail(event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldLabel
                                label="First Name *"
                                value={firstName}
                                error={showWarnings && isEmpty(firstName)}
                                onChange={(event) => setFirstName(event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFieldLabel
                                label="Last Name *"
                                value={lastName}
                                error={showWarnings && isEmpty(lastName)}
                                onChange={(event) => setLastName(event.target.value)}
                            />
                        </Grid>
                    </Grid>
                    <Grid item xs={6}>
                        <SelectInput
                            label="Role *"
                            labelVariant="body1"
                            labelStyle={{ fontSize: '12px' }}
                            options={rolesOptions}
                            value={selectedRoles}
                            onChange={(newValue: any) => {
                                if (Array.isArray(newValue)) {
                                    setSelectedRoles(newValue);
                                } else {
                                    setSelectedRoles([]);
                                }
                            }}
                            sx={{ width: '100%' }}
                            multiple
                            canRemove={false}
                            getOptionLabel={(option: any) => option?.label || ''}
                            renderChip={(option: any) => option?.label || ''}
                        />
                        {showWarnings && selectedRoles.length === 0 && (
                            <Typography color="error" variant="caption" sx={{ ml: 2 }}>
                                Role is required
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={6}>
                        <TextFieldLabel
                            label="Phone"
                            type="tel"
                            value={phone}
                            onChange={(event) => setPhone(event.target.value)}
                            placeholder="Enter phone number"
                            mask={PHONE_MASK_INPUT}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextFieldLabel
                            label="Line 1"
                            value={line1}
                            onChange={(event) => setLine1(event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextFieldLabel
                            label="Line 2"
                            value={line2}
                            onChange={(event) => setLine2(event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextFieldLabel
                            label="City"
                            value={city}
                            onChange={(event) => setCity(event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextFieldLabel
                            label="State"
                            value={state}
                            onChange={(event) => setState(event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextFieldLabel
                            label="ZIP Code"
                            value={zipCode}
                            onChange={(event) => setZipCode(event.target.value)}
                        />
                    </Grid>
                </Grid>
                <Grid container gap={1} display='flex' alignItems='flex-end' justifyContent='flex-end' marginTop='15px'>
                    <Button
                        sx={{ width: '200px' }}
                        variant='outlined'
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                    <Button
                        sx={{ width: '200px' }}
                        variant='contained'
                        onClick={handleCreate}
                        disabled={!isFormValid && showWarnings}
                    >
                        Create
                    </Button>
                </Grid>
            </PrivateLoader>
        </Modal>
    );
}


